import React, { FC, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import styled from 'styled-components';

import SubmitButton from 'components/SubmitButton';
import { useSignInAndApplyOffer } from 'modules/corporateOffer/hooks/useSignInAndApplyOffer';
import { useSignUpFull } from 'modules/corporateOffer/hooks/useSignUpFull';

import CodeInput from '../inputs/CodeInput';
import SuccessResult from '../SuccessResult';

import { FIELDS } from './constants';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';
import { Redline } from './VerifyEmailStep';

type Props = {
  account: string;
  department: string | null;
  email: string;
  pin: string;
};

const SetPasscodeStep: FC<Props> = ({ account, email, department, pin }) => {
  const [error, setError] = useState('');

  const [isCodeFilled, setIsCodeFilled] = useState(false);

  const {
    setFocus,
    handleSubmit,
    control,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm();

  const {
    mutate: signIn,
    isLoading: isSigningIn,
    isError: isSignInError,
  } = useSignInAndApplyOffer(account);
  const {
    mutate: setPasscode,
    isLoading: isSettingPasscode,
    isError: isPasscodeError,
  } = useSignUpFull({
    onSuccess: () => {
      signIn({ email, password: getValues(FIELDS).join('') });
    },
    onError: setError,
  });
  const isLoading = isSettingPasscode || isSigningIn;
  const isError = isPasscodeError || isSignInError;

  const onSubmit = () => {
    setPasscode({
      email,
      pincode: pin,
      termsAndConditions: true,
      passcode: getValues(FIELDS).join(''),
      department,
    });
  };

  return (
    <Container>
      {isLoading ? (
        <SuccessResult title="Account created!" subtitle="Please wait…" />
      ) : (
        <>
          <header>
            <OnboardingTitle title="Choose a passcode" error={isError} />
            <DesktopSubtitle
              subtitle="You will use this to sign in to your Shido mobile app"
              error={isError}
            />
            <MobileSubtitle
              subtitle="You will use this to sign-in to your app"
              error={isError}
            />
            <Redline />
          </header>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <CodeInputWrapper>
              <CodeInput
                label="Choose a 6 digit passcode"
                error={error ? error : ''}
                fields={FIELDS}
                control={control as unknown as Control<FieldValues>}
                setFocus={setFocus}
                setIsCodeFilled={setIsCodeFilled}
                clearError={clearErrors}
              />
            </CodeInputWrapper>
            <ButtonWrapper data-testid="submit-button-wrapper">
              <SubmitButton
                text="CONTINUE"
                isDisabled={
                  Object.keys(errors).length > 0 || isLoading || !isCodeFilled
                }
                borderSize="xl"
                buttonSize="xl"
                fontSize="md"
              />
            </ButtonWrapper>
          </Form>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100%;
  }
`;

const Form = styled.form`
  margin: 70px auto 0;
  max-width: 600px;
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
    margin: 33px auto 0;
    padding-left: 0;
    padding-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
`;

const CodeInputWrapper = styled.div`
  max-width: 300px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
  }
`;

const MobileSubtitle = styled(OnboardingSubtitle)`
  display: none;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: block;
  }
`;

const DesktopSubtitle = styled(OnboardingSubtitle)`
  display: block;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const ButtonWrapper = styled.div`
  margin: 220px auto 0;
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 0;
  }
`;

export default SetPasscodeStep;
